<script setup>
import { ref, computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  src: {
    type: String,
    default: '',
  },
  size: {
    type: Number,
    default: 80,
  },
  placeholderIcon: {
    type: String,
    default: 'i-lucide-message-circle',
  },
});

const emit = defineEmits(['upload', 'delete']);

const fileInput = ref(null);
const isImageValid = ref(true);

const containerStyles = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
}));

const iconStyles = computed(() => ({
  fontSize: `${props.size / 2}px`,
}));

const handleUploadClick = () => {
  fileInput.value.click();
};

const handleImageUpload = event => {
  const [file] = event.target.files;
  if (!file) return;

  // Validate file type
  const validTypes = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/webp',
    'image/svg+xml',
  ];
  if (!validTypes.includes(file.type)) {
    window.bus.$emit('newToastMessage', {
      message: 'Invalid file type. Please upload PNG, JPEG, GIF, WebP, or SVG.',
      type: 'error',
    });
    event.target.value = null;
    return;
  }

  // Validate file size (2MB max)
  const maxSize = 2 * 1024 * 1024; // 2MB in bytes
  if (file.size > maxSize) {
    window.bus.$emit('newToastMessage', {
      message: 'File is too large. Maximum size is 2MB.',
      type: 'error',
    });
    event.target.value = null;
    return;
  }

  // Additional SVG validation
  if (file.type === 'image/svg+xml') {
    const reader = new FileReader();
    reader.onload = e => {
      const svgContent = e.target.result;

      // Check for dangerous patterns
      const dangerousPatterns = [
        /<script/i,
        /on\w+\s*=/i,
        /javascript:/i,
        /data:text\/html/i,
        /<iframe/i,
        /<embed/i,
        /<object/i,
      ];

      const isDangerous = dangerousPatterns.some(pattern =>
        pattern.test(svgContent)
      );

      if (isDangerous) {
        window.bus.$emit('newToastMessage', {
          message:
            'SVG file contains potentially dangerous content and cannot be uploaded.',
          type: 'error',
        });
        event.target.value = null;
        return;
      }

      // SVG is safe, proceed with upload
      isImageValid.value = true;
      emit('upload', {
        file,
        url: URL.createObjectURL(file),
      });
    };
    reader.readAsText(file);
  } else {
    // Non-SVG image, proceed
    isImageValid.value = true;
    emit('upload', {
      file,
      url: URL.createObjectURL(file),
    });
  }
};

const handleDelete = event => {
  event.stopPropagation();
  if (fileInput.value) {
    fileInput.value.value = null;
  }
  isImageValid.value = true;
  emit('delete');
};

const invalidateCurrentImage = () => {
  isImageValid.value = false;
};
</script>

<template>
  <div
    class="relative inline-flex group/icon-uploader flex-shrink-0"
    :style="containerStyles"
  >
    <!-- Delete Button -->
    <div
      v-if="src && isImageValid"
      class="absolute z-20 flex items-center justify-center invisible w-6 h-6 transition-all duration-300 ease-in-out opacity-0 cursor-pointer outline outline-1 outline-n-container -top-2 -right-2 rounded-xl bg-n-solid-3 group-hover/icon-uploader:visible group-hover/icon-uploader:opacity-100"
      @click="handleDelete"
    >
      <Icon icon="i-lucide-x" class="text-n-slate-11 size-4" />
    </div>

    <!-- Icon Container -->
    <div
      class="relative inline-flex items-center justify-center object-cover overflow-hidden rounded-full bg-n-slate-3 dark:bg-n-slate-4 cursor-pointer"
      :style="containerStyles"
      @click="handleUploadClick"
    >
      <!-- Uploaded Image -->
      <img
        v-if="src && isImageValid"
        :src="src"
        alt="Icon"
        class="w-full h-full object-cover"
        @error="invalidateCurrentImage"
      />

      <!-- Placeholder Icon -->
      <Icon
        v-else
        :icon="placeholderIcon"
        class="text-n-slate-11"
        :style="iconStyles"
      />

      <!-- Upload Overlay -->
      <div
        class="absolute inset-0 z-10 flex items-center justify-center invisible w-full h-full transition-all duration-300 ease-in-out opacity-0 rounded-full bg-n-alpha-black1 group-hover/icon-uploader:visible group-hover/icon-uploader:opacity-100"
      >
        <Icon
          icon="i-lucide-upload"
          class="text-white"
          :style="{ width: `${size / 2}px`, height: `${size / 2}px` }"
        />
      </div>

      <!-- Hidden File Input -->
      <input
        ref="fileInput"
        type="file"
        accept="image/png,image/svg+xml,image/jpeg,image/gif,image/webp"
        class="hidden"
        @change="handleImageUpload"
      />
    </div>
  </div>
</template>
